<template>
  <layout ref="layout"
          :active-item="activeItemNumber"
          :child-route="displaySubtitle"
          :class="$setClasses"
          :display-nav-header="displayNavHeader"
          :items="headerSteps"
          :parent-route="parentRoute"
          :subtitle="displaySubtitle"
          :title="$t('base.step.title')"
          :total-routes="totalRoutes"
          display-current-index
          display-progress
          hide-action-bar>
    <transition :name="$vPrefix('fade')"
                mode="out-in">
      <div v-if="!isLoading"
           :id="`${currentStep.id}-step`"
           :key="currentStepNb"
           :class="[$vClass('body'), { [$vPrefix('--is-panel-right')]: currentStep.right_panel_content }]">
        <panel v-if="currentStep.left_panel_content"
               :class="$vClass('left-panel')"
               :content="currentStep.left_panel_content"/>
        <vee-form ref="form"
                  :class="$vClass('content')">
          <form id="form"
                :class="$vClass('content__form')"
                @submit.prevent="submitForm()">
            <slot/>
          </form>
          <nxt-core-text-content v-if="messageError"
                                 :class="[$vClass('content__error'), $vPrefix('text--error')]"
                                 tag="div">
            {{ messageError }}
          </nxt-core-text-content>
        </vee-form>
        <div ref="action-bar"
             :class="$vClass('action-bar')">
          <nxt-core-action-bar v-if="!(typeof currentStep.button_next === 'boolean'
             && !currentStep.button_next && !displayBackButton)"
                               :class="$vClass('action-bar__content')"
                               :display-back-button="displayBackButton"
                               :display-next-button="displayNextButton"
                               :show-next="!isLastStepForm"
                               :text-back-button="$t('base.back')"
                               :text-next-button="currentStep && currentStep.text_button_next || $t('base.next')"
                               :wait-show-next="!!isLastStepForm"
                               type-next-button="submit"
                               @back="backStep"/>
        </div>
        <panel v-if="currentStep.right_panel_content"
               :class="$vClass('right-panel')"
               :content="currentStep.right_panel_content"/>
      </div>
      <div v-else>
        <nxt-core-loader size-loader="--size-small"/>
      </div>
    </transition>
  </layout>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useFurnituresStore } from '@nextories/vue-core-components/src/store/furnituresStore.ts';
import { usePartnerStore } from '../../store/partnerStore.ts';

export default {
  name: 'StepManager',
  nameClassStyle: 'step-manager',
  data() {
    return {
      displayNextButton: true,
      stepNbAtSaveStart: null,
    };
  },
  computed: {
    ...mapState(useFurnituresStore, ['rooms']),
    ...mapState(usePartnerStore, [
      'scrollFields',
      'currentStepNb',
      'projectToken',
      'typeMeeting',
      'totalSteps',
      'allowEstimate',
      'isDataSaving',
      'fixedHeaderHeight',
      'steps',
      'onlineBookingLoading',
      'dataError',
      'messageError',
      'currentStep',
      'scrollYAuto',
    ]),
    formattedRooms() {
      const rooms = {};
      for (const room of this.rooms) {
        if (room?.meubles?.length) {
          rooms[room.name] = room.meubles.map((meuble) => ({
            name: meuble.name,
            volume: meuble.volume,
            count: meuble.quantity,
          }));
        }
      }
      return rooms;
    },
    isLoading() {
      return this.isDataSaving || this.onlineBookingLoading;
    },
    displaySubtitle() {
      return this.currentStep?.display_name || '';
    },
    displayBackButton() {
      return this.currentStepNb > 0;
    },
    parentRoute() {
      let previewStepIndex = this.currentStepNb - 1;
      if (this.currentStep?.previous_step) {
        const findIndexStep = this.steps.findIndex((step) => step.id
          === this.currentStep.previous_step);
        if (findIndexStep !== -1) {
          previewStepIndex = findIndexStep;
        }
      }
      return this.steps[previewStepIndex]?.display_name || '';
    },
    totalRoutes() {
      return this.headerSteps.length;
    },
    displayNavHeader() {
      return !this.currentStep.hide_header;
    },
    activeItemNumber() {
      return this.currentStep.display_number
        ? this.currentStep.display_number - 1
        : this.currentStepNb;
    },
    isLastStepForm() {
      return this.currentStepNb + 1 === this.totalSteps;
    },
    headerSteps() {
      return this.steps.filter((step) => step && !step.hide_header);
    },
  },
  watch: {
    isDataSaving(value) {
      if (value) {
        this.stepNbAtSaveStart = this.currentStepNb;
        setTimeout(() => this.scrollStep(true), 300);
      }
      if (!value && !this.dataError) {
        if (this.stepNbAtSaveStart !== this.currentStepNb) {
          this.stepNbAtSaveStart = null;
          return;
        }
        this.stepNbAtSaveStart = null;
        let nextStep;
        const findTypeMeting = this.currentStep?.fields?.find((field) => field.type === 'type-meeting');
        if (findTypeMeting) {
          if (this.typeMeeting && this.typeMeeting.value === 'ONLINE') {
            this.getOnlineBooking();
            return;
          }
          nextStep = this.typeMeeting.next_step;
        }
        this.nextStep(nextStep);
      }
    },
    currentStepNb(nb, old) {
      this.displayNextButton = this.showNextButton();
      if (this.scrollYAuto) {
        setTimeout(() => this.scrollStep(nb > old), nb > old ? 300 : 400);
      }
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.currentStep) {
            this.currentStep.readyForAutoScroll = true;
          }
        }, 500);
      });
    },
  },
  created() {
    this.$helpers['event-bus'].$on('scrollField', ({
      el,
      behavior,
      space,
    }) => this.scrollFields && this.scrollElement(el, behavior, space));
    this.$helpers['event-bus'].$on('validateForm', (data) => {
      if (data) {
        this.submitForm(data.argument);
      } else if (this.$refs?.form) this.$refs.form.validate();
    });
    this.$helpers['event-bus'].$on('showNextButton', () => {
      if (this.currentStep?.button_next?.ref_fields?.length) {
        this.currentStep.button_next.value = this.$helpers.fieldUtils.isValidRefFields(
          this.currentStep.button_next.ref_fields,
          [this.currentStep],
        );
        this.displayNextButton = !!this.showNextButton();
      }
    });
    this.displayNextButton = this.showNextButton();
  },
  methods: {
    ...mapActions(usePartnerStore, [
      'postProject',
      'updateProject',
      'getEstimation',
      'getOnlineBooking',
      'postAllowEstimate',
      'postCalcMinarmRights',
      'setActiveStep',
    ]),
    async submitForm(action) {
      this.currentStep.formSubmitted = true;
      const form = this.$refs?.form && await this.$refs.form.validate();
      if ((form?.valid === false) || !form) {
        const inputsContainer = document.getElementsByClassName('is-invalid');
        if (inputsContainer?.[0]) {
          const input = inputsContainer[0];
          const inputRect = input.getBoundingClientRect();
          if (inputRect.top < 0) {
            window.scroll({
              top: inputRect.top + window.scrollY - this.fixedHeaderHeight - 60,
              behavior: 'smooth',
            });
          }
          // Move focus on to the first input where there is error
          const elInput = inputsContainer[0].getElementsByClassName('el-input__inner');
          if (elInput?.[0]) {
            elInput[0].focus();
          }
        }
      } else {
        await this.actionStep(action);
      }
    },
    showNextButton() {
      const button = this.currentStep?.button_next;
      return !!((typeof button !== 'boolean' && typeof button !== 'object')
        || ((typeof button === 'boolean' && button) || button.value));
    },
    backStep() {
      if (this.currentStep) {
        this.currentStep.readyForAutoScroll = false;
        this.setActiveStep({
          currentStepNb: this.currentStepNb - 1,
          nextStep: this.currentStep.previous_step,
        });
      }
    },
    actionStep(action) {
      action = action || this.currentStep.action;
      if (action && this[action]) {
        this[action]();
        return;
      }
      this.nextStep();
    },
    async get_estimation() {
      try {
        await this.getEstimation(this.cleandData());
      } catch (error) {
        console.error(error);
      }
    },
    async save_data() {
      try {
        const data = this.cleandData();
        data.send_mail = this.resolveSendMail();
        const typeMethod = this.projectToken ? this.updateProject : this.postProject;
        await typeMethod(data);
      } catch (error) {
        console.error(error);
      }
    },
    async check_allow_estimate() {
      try {
        await this.postAllowEstimate(this.$helpers.fieldUtils.cleanData([this.currentStep]));
      } catch (error) {
        console.error(error);
      }
    },
    async calc_minarm_rights() {
      try {
        await this.postCalcMinarmRights(this.cleandData());
      } catch (error) {
        console.error(error);
      }
    },
    nextStep(nextStep) {
      if (this.currentStep) {
        this.currentStep.readyForAutoScroll = false;
        this.setActiveStep({
          currentStepNb: this.currentStepNb + 1,
          nextStep: nextStep || this.currentStep?.next_step,
        });
      }
    },
    scrollStep(top) {
      if (top) {
        if (this.$refs?.layout?.$refs?.['nav-header']) {
          const navHeader = this.$refs.layout.$refs['nav-header'].getBoundingClientRect();
          if (navHeader.top < 0) {
            this.scrollWithBehavior(navHeader.top + window.pageYOffset - this.fixedHeaderHeight, 'auto');
          }
        }
        return;
      }
      if (this.$refs?.['action-bar']) {
        this.scrollElement(this.$refs['action-bar']);
      }
    },
    scrollWithBehavior(scrollTo, behavior) {
      const html = document.querySelector('html');
      const previousBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = behavior;
      window.scroll({
        top: scrollTo,
        behavior,
      });
      html.style.scrollBehavior = previousBehavior;
    },
    scrollElement(el, behavior, space) {
      if (el) {
        const elementRect = el.getBoundingClientRect();
        if (elementRect) {
          this.scrollWithBehavior(
            elementRect.top + el.ownerDocument.defaultView.pageYOffset
            - window.innerHeight + elementRect.height + (space || 0),
            behavior || 'auto',
          );
        }
      }
    },
    cleandData() {
      const data = this.$helpers.fieldUtils.cleanData(this.steps);
      if (data.formData && Object.keys(this.formattedRooms).length) {
        data.formData.meubles = this.formattedRooms;
      }
      return data;
    },
    resolveSendMail() {
      return this.currentStep?.send_welcome_mail ?? this.isSingleStepFormWithConfirm();
    },
    isSingleStepFormWithConfirm() {
      if (this.totalSteps !== 2 || !this.steps?.length) {
        return false;
      }
      const hasConfirmStep = this.steps.some((step) => step.fields?.some((field) => field.type === 'confirm'));
      const saveDataStepsCount = this.steps.filter((step) => step.action === 'save_data').length;
      return hasConfirmStep && saveDataStepsCount === 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.#{$prefixName}step-manager {
  &__body {
    padding: 0;
    @include breakpoint(tablet) {
      display: grid;
      column-gap: var(--nxt-space-large);
      grid-template-columns: auto 1fr auto;
      grid-template-rows: auto auto 1fr;
      grid-template-areas:
      "loader loader loader"
      "left-panel content right-panel"
      "left-panel action-bar right-panel";
    }
    @include breakpoint(tablet) {
      :deep(.#{$prefixName}inventory) {
        .#{$prefixName}inventory__header .#{$prefixName}inline-selector__list {
          max-width: calc(100vw - 60px - 125px);
        }

        .#{$prefixName}furniture-selector .#{$prefixName}inline-selector__list {
          max-width: calc(100vw - 130px);
        }
      }
    }

    &.#{$prefixName}--is-panel-right {
      @include breakpoint(tablet) {
        :deep(.#{$prefixName}inventory) {
          .#{$prefixName}inventory__header .#{$prefixName}inline-selector__list {
            max-width: calc(65vw - 60px - 125px);
          }

          .#{$prefixName}furniture-selector .#{$prefixName}inline-selector__list {
            max-width: calc(65vw - 130px);
          }
        }
      }
    }
  }

  &__left-panel {
    grid-area: left-panel;
    font-size: var(--nxt-font-size-medium);
    @include breakpoint(tablet) {
      margin-left: var(--nxt-space-large);
    }
  }

  &__content {
    grid-area: content;

    &__form {
      margin: auto;
    }
  }

  &__right-panel {
    grid-area: right-panel;
    font-size: var(--nxt-font-size-medium);
    border-bottom-right-radius: 10px;
    border-bottom-left-radius: 10px;
    @include breakpoint(tablet) {
      border-bottom-left-radius: 0;
    }
  }

  &__action-bar {
    grid-area: action-bar;
    width: 100%;

    :deep(.nxt-action-bar__button--next),
    :deep(.nxt-action-bar__button--back) {
      text-align: center;
    }
  }

  &__loader {
    grid-area: loader;
  }

  &__content__form, &__action-bar__content {
    padding: var(--nxt-space-large) 10px;
    @include breakpoint(tablet) {
      margin: auto;
    }
  }

  &__content__error {
    margin: 0;
    padding: 0 10px;
  }
}

.#{$prefixName}fade {
  &-enter-active, &-leave-active {
    transition: opacity 300ms;
  }

  &-enter-from, &-leave-to {
    opacity: 0;
  }
}
</style>
